/**
 * Web Presence Module Types
 * Type definitions for sites, pages, forms, landing pages, and content blocks
 */

// ============================================================================
// SITE TYPES
// ============================================================================

export interface Site {
  id: string;
  tenantId: string;
  siteName: string;
  domain: string | null;
  subdomain: string | null;
  siteType: SiteType;
  themeId: string | null;
  settings: SiteSettings | null;
  isPublished: boolean;
  customCss: string | null;
  customJs: string | null;
  headScripts: string | null;
  footerScripts: string | null;
  createdAt: string;
  updatedAt: string;
}

export type SiteType =
  | 'website'
  | 'landing_page_collection'
  | 'blog'
  | 'portal'
  | 'microsite';

export interface SiteSettings {
  favicon?: string;
  logo?: string;
  logoAlt?: string;
  primaryColor?: string;
  secondaryColor?: string;
  fontFamily?: string;
  headerStyle?: 'fixed' | 'static' | 'sticky';
  footerContent?: string;
  socialLinks?: SocialLink[];
  analytics?: AnalyticsSettings;
}

export interface SocialLink {
  platform: string;
  url: string;
  icon?: string;
}

export interface AnalyticsSettings {
  googleAnalyticsId?: string;
  googleTagManagerId?: string;
  facebookPixelId?: string;
  customScripts?: string[];
}

export interface CreateSiteRequest {
  siteName: string;
  domain?: string;
  subdomain?: string;
  siteType?: SiteType;
  themeId?: string;
  settings?: SiteSettings;
  customCss?: string;
  customJs?: string;
}

export interface UpdateSiteRequest {
  siteName?: string;
  domain?: string;
  subdomain?: string;
  siteType?: SiteType;
  themeId?: string;
  settings?: SiteSettings;
  isPublished?: boolean;
  customCss?: string;
  customJs?: string;
}

// ============================================================================
// PAGE TYPES
// ============================================================================

export interface Page {
  id: string;
  siteId: string;
  pageTitle: string;
  pageSlug: string;
  pageType: PageType;
  templateId: string | null;
  parentPageId: string | null;
  contentBlocks: ContentBlock[];
  metaTitle: string | null;
  metaDescription: string | null;
  featuredImageUrl: string | null;
  sortOrder: number;
  status: PageStatus;
  version: number;
  publishedAt: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  // Per-page script injection (Phase 3)
  headScripts?: string;
  bodyStartScripts?: string;
  bodyEndScripts?: string;
  // Approval workflow (Phase 4)
  approvalStatus?: 'none' | 'pending_review' | 'approved' | 'rejected';
  reviewerId?: string | null;
  reviewNotes?: string | null;
  reviewedAt?: string | null;
}

export type PageType =
  | 'page'
  | 'blog_post'
  | 'landing_page'
  | 'product_page'
  | 'category_page'
  | 'contact_page'
  | 'about_page';

export type PageStatus =
  | 'draft'
  | 'published'
  | 'scheduled'
  | 'archived';

export interface CreatePageRequest {
  siteId: string;
  pageTitle: string;
  pageSlug: string;
  pageType?: PageType;
  templateId?: string;
  parentPageId?: string;
  contentBlocks?: ContentBlock[];
  metaTitle?: string;
  metaDescription?: string;
  featuredImageUrl?: string;
  sortOrder?: number;
}

export interface UpdatePageRequest {
  pageTitle?: string;
  pageSlug?: string;
  pageType?: PageType;
  templateId?: string;
  parentPageId?: string;
  contentBlocks?: ContentBlock[];
  metaTitle?: string;
  metaDescription?: string;
  featuredImageUrl?: string;
  sortOrder?: number;
  status?: PageStatus;
}

// ============================================================================
// CONTENT BLOCK TYPES
// ============================================================================

export interface ContentBlock {
  id: string;
  pageId: string;
  parentBlockId: string | null;
  blockType: BlockType;
  blockName: string | null;
  content: BlockContent;
  styles: BlockStyles | null;
  settings: BlockSettings | null;
  sortOrder: number;
  isVisible: boolean;
  createdAt: string;
  updatedAt: string;
}

export type BlockType =
  | 'hero'
  | 'text'
  | 'richtext'
  | 'image'
  | 'video'
  | 'gallery'
  | 'cta'
  | 'form'
  | 'testimonials'
  | 'features'
  | 'pricing'
  | 'faq'
  | 'team'
  | 'contact'
  | 'map'
  | 'social'
  | 'divider'
  | 'spacer'
  | 'columns'
  | 'container'
  | 'html'
  | 'embed'
  | 'custom_code'
  | 'collection_list'
  | 'component';

export interface BlockContent {
  // Hero block
  headline?: string;
  subheadline?: string;
  backgroundImage?: string;
  backgroundVideo?: string;
  ctaButtons?: CTAButton[];

  // Text block
  text?: string;
  richText?: string;

  // Image block
  imageUrl?: string;
  imageAlt?: string;
  caption?: string;
  linkUrl?: string;

  // Video block
  videoUrl?: string;
  videoType?: 'youtube' | 'vimeo' | 'hosted';
  autoplay?: boolean;
  muted?: boolean;

  // Gallery block
  images?: GalleryImage[];
  layout?: 'grid' | 'masonry' | 'carousel';

  // CTA block
  title?: string;
  description?: string;
  buttons?: CTAButton[];

  // Form block
  formId?: string;

  // Testimonials block
  testimonials?: Testimonial[];

  // Features block
  features?: Feature[];

  // Pricing block
  plans?: PricingPlan[];

  // FAQ block
  faqs?: FAQItem[];

  // Team block
  members?: TeamMember[];

  // Contact block
  address?: string;
  phone?: string;
  email?: string;
  hours?: string;

  // Map block
  latitude?: number;
  longitude?: number;
  zoom?: number;
  markerTitle?: string;

  // Social block
  socialLinks?: SocialLink[];

  // HTML block
  html?: string;

  // Embed block
  embedCode?: string;
  embedUrl?: string;

  // Columns/Container
  columns?: number;
  columnWidths?: string[];
  children?: ContentBlock[];

  // Custom Code block (Phase 3)
  customHtml?: string;
  customCss?: string;
  customJs?: string;

  // Collection List block (Phase 2)
  collectionConfig?: {
    contentTypeId: string;
    filters?: CollectionFilter[];
    sort?: { field: string; direction: 'asc' | 'desc' };
    limit?: number;
    itemTemplate?: ContentBlock[];
  };

  // Component block (Phase 2)
  componentId?: string;
  componentProps?: Record<string, any>;
  componentVariant?: string;
}

export interface CTAButton {
  id: string;
  text: string;
  url: string;
  style: 'primary' | 'secondary' | 'outline' | 'text';
  target?: '_self' | '_blank';
  icon?: string;
}

export interface GalleryImage {
  id: string;
  url: string;
  alt: string;
  caption?: string;
}

export interface Testimonial {
  id: string;
  quote: string;
  author: string;
  role?: string;
  company?: string;
  avatar?: string;
  rating?: number;
}

export interface Feature {
  id: string;
  icon?: string;
  title: string;
  description: string;
  link?: string;
}

export interface PricingPlan {
  id: string;
  name: string;
  price: string;
  period?: string;
  description?: string;
  features: string[];
  ctaText: string;
  ctaUrl: string;
  isPopular?: boolean;
}

export interface FAQItem {
  id: string;
  question: string;
  answer: string;
}

export interface TeamMember {
  id: string;
  name: string;
  role: string;
  bio?: string;
  photo?: string;
  socialLinks?: SocialLink[];
}

export interface BlockStyles {
  // Background
  backgroundColor?: string;
  backgroundImage?: string;
  backgroundGradient?: string;
  backgroundSize?: string;
  backgroundPosition?: string;
  textColor?: string;

  // Layout
  display?: string;
  flexDirection?: string;
  justifyContent?: string;
  alignItems?: string;
  gap?: string;
  gridTemplateColumns?: string;

  // Spacing (shorthand)
  padding?: string;
  margin?: string;

  // Spacing (per-side)
  paddingTop?: string;
  paddingRight?: string;
  paddingBottom?: string;
  paddingLeft?: string;
  marginTop?: string;
  marginRight?: string;
  marginBottom?: string;
  marginLeft?: string;

  // Size
  width?: string;
  height?: string;
  minWidth?: string;
  maxWidth?: string;
  minHeight?: string;
  maxHeight?: string;
  overflow?: string;

  // Typography
  fontFamily?: string;
  fontSize?: string;
  fontWeight?: string;
  lineHeight?: string;
  letterSpacing?: string;
  color?: string;
  textAlign?: 'left' | 'center' | 'right' | 'justify';
  textDecoration?: string;

  // Border
  borderWidth?: string;
  borderStyle?: string;
  borderColor?: string;
  borderRadius?: string;
  borderTopLeftRadius?: string;
  borderTopRightRadius?: string;
  borderBottomLeftRadius?: string;
  borderBottomRightRadius?: string;

  // Effects
  boxShadow?: string;
  opacity?: string;
  transform?: string;
  transition?: string;

  // Custom
  customCss?: string;
  animation?: string;
}

export interface BlockSettings {
  isFullWidth?: boolean;
  isHidden?: boolean;
  showOnMobile?: boolean;
  showOnTablet?: boolean;
  showOnDesktop?: boolean;
  anchorId?: string;
  className?: string;
  responsiveStyles?: {
    mobile?: Partial<BlockStyles>;
    tablet?: Partial<BlockStyles>;
    desktop?: Partial<BlockStyles>;
  };
  // Data binding (Phase 2)
  dataBinding?: {
    contentTypeId: string;
    fieldMappings: Record<string, string>; // blockProp -> contentField
  };
}

export interface CreateBlockRequest {
  pageId: string;
  parentBlockId?: string;
  blockType: BlockType;
  blockName?: string;
  content: BlockContent;
  styles?: BlockStyles;
  settings?: BlockSettings;
  sortOrder?: number;
}

export interface UpdateBlockRequest {
  blockName?: string;
  content?: BlockContent;
  styles?: BlockStyles;
  settings?: BlockSettings;
  sortOrder?: number;
  isVisible?: boolean;
}

// ============================================================================
// FORM TYPES
// ============================================================================

export interface Form {
  id: string;
  tenantId: string;
  siteId: string | null;
  formName: string;
  formType: FormType;
  fields: FormField[];
  settings: FormSettings | null;
  successMessage: string | null;
  redirectUrl: string | null;
  notificationEmails: string[] | null;
  isActive: boolean;
  submissionCount: number;
  createdAt: string;
  updatedAt: string;
}

export type FormType =
  | 'contact'
  | 'lead_capture'
  | 'newsletter'
  | 'registration'
  | 'survey'
  | 'feedback'
  | 'appointment'
  | 'quote_request'
  | 'custom';

export interface FormField {
  id: string;
  type: FieldType;
  label: string;
  name: string;
  required: boolean;
  placeholder?: string;
  helpText?: string;
  defaultValue?: string;
  options?: FieldOption[];
  validation?: FieldValidation;
  conditionalLogic?: ConditionalLogic;
  width?: 'full' | 'half' | 'third';
  /** Tracks where this field was imported from (member config) */
  importSource?: {
    fieldKey: string;
    isCoreField: boolean;
    configFieldType: string; // original type from config (e.g. 'str', 'select')
  };
  /** When type='field_group', references the backend field_groups.id and exposes
   *  metadata so the renderer can show child counts / repeatability in the canvas
   *  without re-fetching. The runtime renderer always re-fetches the live group. */
  fieldGroupId?: number;
  fieldGroupMeta?: {
    name: string;
    isRepeatable: boolean;
    childCount: number;
  };
}

export type FieldType =
  | 'text'
  | 'email'
  | 'phone'
  | 'number'
  | 'textarea'
  | 'select'
  | 'radio'
  | 'checkbox'
  | 'date'
  | 'time'
  | 'datetime'
  | 'file'
  | 'hidden'
  | 'heading'
  | 'paragraph'
  | 'divider'
  | 'field_group';

export interface FieldOption {
  label: string;
  value: string;
}

export interface FieldValidation {
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  patternMessage?: string;
  min?: number;
  max?: number;
  allowedFileTypes?: string[];
  maxFileSize?: number;
}

export interface ConditionalLogic {
  enabled: boolean;
  action: 'show' | 'hide';
  rules: ConditionalRule[];
  match: 'all' | 'any';
}

export interface ConditionalRule {
  fieldId: string;
  operator: 'equals' | 'not_equals' | 'contains' | 'not_contains' | 'is_empty' | 'not_empty';
  value: string;
}

export interface FormSettings {
  submitButtonText?: string;
  submitButtonStyle?: 'primary' | 'secondary';
  showLabels?: boolean;
  layout?: 'vertical' | 'horizontal';
  recaptchaEnabled?: boolean;
  recaptchaSiteKey?: string;
  honeypotEnabled?: boolean;
  doubleOptIn?: boolean;
  autoResponderEnabled?: boolean;
  autoResponderSubject?: string;
  autoResponderBody?: string;
}

export interface FormSubmission {
  id: string;
  formId: string;
  data: Record<string, any>;
  sourceUrl: string | null;
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  submittedAt: string;
  isRead: boolean;
  contactId: string | null;
}

export interface CreateFormRequest {
  siteId?: string;
  formName: string;
  formType?: FormType;
  fields: FormField[];
  settings?: FormSettings;
  successMessage?: string;
  redirectUrl?: string;
  notificationEmails?: string[];
}

export interface UpdateFormRequest {
  formName?: string;
  formType?: FormType;
  fields?: FormField[];
  settings?: FormSettings;
  successMessage?: string;
  redirectUrl?: string;
  notificationEmails?: string[];
  isActive?: boolean;
}

export interface SubmitFormRequest {
  data: Record<string, any>;
  sourceUrl?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
}

// ============================================================================
// LANDING PAGE TYPES
// ============================================================================

export interface LandingPage {
  id: string;
  tenantId: string;
  campaignId: string | null;
  pageName: string;
  pageSlug: string;
  templateId: string | null;
  contentBlocks: ContentBlock[];
  metaTitle: string | null;
  metaDescription: string | null;
  conversionGoal: string | null;
  settings: LandingPageSettings | null;
  status: PageStatus;
  views: number;
  conversions: number;
  conversionRate: number;
  parentPageId: string | null;
  isVariant: boolean;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface LandingPageSettings {
  showHeader?: boolean;
  showFooter?: boolean;
  customDomain?: string;
  expirationDate?: string;
  passwordProtected?: boolean;
  password?: string;
  conversionTracking?: {
    enabled: boolean;
    conversionEvent?: string;
    conversionValue?: number;
  };
  abTestingConfig?: ABTestConfig;
}

export interface ABTestConfig {
  enabled: boolean;
  variantAllocation?: number; // Percentage of traffic to variant (0-100)
  startDate?: string;
  endDate?: string;
  winnerMetric?: 'conversions' | 'conversion_rate' | 'views';
}

export interface CreateLandingPageRequest {
  campaignId?: string;
  pageName: string;
  pageSlug: string;
  templateId?: string;
  contentBlocks?: ContentBlock[];
  metaTitle?: string;
  metaDescription?: string;
  conversionGoal?: string;
  settings?: LandingPageSettings;
}

export interface UpdateLandingPageRequest {
  pageName?: string;
  pageSlug?: string;
  templateId?: string;
  contentBlocks?: ContentBlock[];
  metaTitle?: string;
  metaDescription?: string;
  conversionGoal?: string;
  settings?: LandingPageSettings;
  status?: PageStatus;
}

// ============================================================================
// MENU TYPES
// ============================================================================

export interface Menu {
  id: string;
  siteId: string;
  menuName: string;
  menuLocation: MenuLocation;
  items: MenuItem[];
  createdAt: string;
  updatedAt: string;
}

export type MenuLocation =
  | 'header'
  | 'footer'
  | 'sidebar'
  | 'mobile'
  | 'custom';

export interface MenuItem {
  id: string;
  label: string;
  url?: string;
  pageId?: string;
  target: '_self' | '_blank';
  icon?: string;
  children?: MenuItem[];
}

export interface CreateMenuRequest {
  siteId: string;
  menuName: string;
  menuLocation?: MenuLocation;
  items?: MenuItem[];
}

export interface UpdateMenuRequest {
  menuName?: string;
  menuLocation?: MenuLocation;
  items?: MenuItem[];
}

// ============================================================================
// SEO TYPES
// ============================================================================

export interface SEOConfig {
  id: string;
  siteId: string;
  defaultTitleTemplate: string | null;
  defaultMetaDescription: string | null;
  ogImageUrl: string | null;
  twitterHandle: string | null;
  googleAnalyticsId: string | null;
  googleTagManagerId: string | null;
  facebookPixelId: string | null;
  robotsTxt: string | null;
  customHeadTags: string | null;
  structuredData: Record<string, any> | null;
  sitemapEnabled: boolean;
  updatedAt: string;
}

export interface UpdateSEOConfigRequest {
  defaultTitleTemplate?: string;
  defaultMetaDescription?: string;
  ogImageUrl?: string;
  twitterHandle?: string;
  googleAnalyticsId?: string;
  googleTagManagerId?: string;
  facebookPixelId?: string;
  robotsTxt?: string;
  customHeadTags?: string;
  structuredData?: Record<string, any>;
  sitemapEnabled?: boolean;
}

// ============================================================================
// ASSET TYPES
// ============================================================================

export interface Asset {
  id: string;
  tenantId: string;
  fileName: string;
  fileUrl: string;
  fileSize: number;
  mimeType: string;
  assetType: AssetType;
  altText: string | null;
  title: string | null;
  dimensions: ImageDimensions | null;
  tags: string[] | null;
  folder: string | null;
  uploadedBy: string | null;
  createdAt: string;
}

export type AssetType =
  | 'image'
  | 'video'
  | 'audio'
  | 'document'
  | 'file';

export interface ImageDimensions {
  width: number;
  height: number;
}

export interface AssetFolder {
  name: string;
  path: string;
  assetCount: number;
}

// ============================================================================
// THEME TYPES
// ============================================================================

export interface Theme {
  id: string;
  themeName: string;
  themeSlug: string;
  themeType: ThemeType;
  description: string | null;
  thumbnailUrl: string | null;
  previewUrl: string | null;
  colorScheme: ColorScheme;
  typography: Typography;
  layoutConfig: LayoutConfig;
  customCss: string | null;
  isActive: boolean;
  createdAt: string;
}

export type ThemeType = 'system' | 'premium' | 'custom';

export interface ColorScheme {
  primary: string;
  secondary: string;
  accent: string;
  background: string;
  surface: string;
  text: string;
  textLight: string;
  error: string;
  success: string;
  warning: string;
}

export interface Typography {
  headingFont: string;
  bodyFont: string;
  baseSize: string;
  headingSizes: {
    h1: string;
    h2: string;
    h3: string;
    h4: string;
    h5: string;
    h6: string;
  };
  lineHeight: string;
  letterSpacing: string;
}

export interface LayoutConfig {
  maxWidth: string;
  containerPadding: string;
  sectionSpacing: string;
  gridGap: string;
  borderRadius: string;
  boxShadow: string;
}

// ============================================================================
// BLOCK TEMPLATE TYPES
// ============================================================================

export interface BlockTemplate {
  id: string;
  templateName: string;
  templateSlug: string;
  blockType: BlockType;
  category: string;
  description: string | null;
  thumbnailUrl: string | null;
  defaultContent: BlockContent;
  defaultStyles: BlockStyles;
  defaultSettings: BlockSettings;
  sortOrder: number;
  isActive: boolean;
}

// ============================================================================
// CHANNEL PORTAL TYPES
// ============================================================================

export interface ChannelPortal {
  id: string;
  siteId: string;
  channelId: string;
  portalName: string;
  portalSlug: string;
  settings: ChannelPortalSettings;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ChannelPortalSettings {
  allowPartnerBranding?: boolean;
  partnerDashboardEnabled?: boolean;
  leadCapture?: {
    enabled: boolean;
    formId?: string;
    autoAssign?: boolean;
  };
  contentAccess?: {
    publicPages?: string[];
    partnerOnlyPages?: string[];
  };
}

// ============================================================================
// ANALYTICS TYPES
// ============================================================================

export interface PageAnalytics {
  id: string;
  pageId: string;
  pageType: 'page' | 'landing_page';
  eventDate: string;
  views: number;
  uniqueVisitors: number;
  avgTimeOnPage: number;
  bounceRate: number;
  conversionEvents: number;
  scrollDepth: number;
}

export interface PageAnalyticsSummary {
  totalViews: number;
  totalUniqueVisitors: number;
  avgTimeOnPage: number;
  avgBounceRate: number;
  totalConversions: number;
  conversionRate: number;
  topPages: {
    pageId: string;
    pageTitle: string;
    views: number;
  }[];
  dailyStats: {
    date: string;
    views: number;
    conversions: number;
  }[];
}

// ============================================================================
// CONTENT TYPE TYPES (Phase 2)
// ============================================================================

export interface ContentType {
  id: string;
  typeName: string;
  typeSlug: string;
  description: string | null;
  icon: string | null;
  fields: ContentField[];
  singleton: boolean;
  sortFields: string[];
  displayField: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
}

export interface ContentField {
  id: string;
  name: string;
  label: string;
  type: ContentFieldType;
  required: boolean;
  defaultValue?: any;
  options?: FieldOption[];
  validation?: FieldValidation;
  referenceTypeId?: string;
}

export type ContentFieldType =
  | 'text'
  | 'richtext'
  | 'number'
  | 'boolean'
  | 'date'
  | 'datetime'
  | 'image'
  | 'file'
  | 'url'
  | 'email'
  | 'color'
  | 'select'
  | 'multi_select'
  | 'reference'
  | 'multi_reference'
  | 'json';

export interface ContentItem {
  id: string;
  contentTypeId: string;
  data: Record<string, any>;
  slug: string | null;
  status: 'draft' | 'published' | 'archived';
  publishedAt: string | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
  updatedBy: string | null;
}

export interface ContentReference {
  id: string;
  sourceItemId: string;
  sourceField: string;
  targetItemId: string;
  targetTypeId: string;
  sortOrder: number;
}

export interface CollectionFilter {
  field: string;
  operator: 'equals' | 'not_equals' | 'contains' | 'gt' | 'lt' | 'gte' | 'lte' | 'in';
  value: any;
}

// ============================================================================
// COMPONENT TYPES (Phase 2)
// ============================================================================

export interface WebComponent {
  id: string;
  componentName: string;
  componentSlug: string;
  description: string | null;
  category: string | null;
  blocks: ContentBlock[];
  propsSchema: ComponentProp[];
  styleVariants: StyleVariant[];
  thumbnailUrl: string | null;
  isGlobal: boolean;
  usageCount: number;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
}

export interface ComponentProp {
  id: string;
  name: string;
  label: string;
  type: 'text' | 'richtext' | 'image' | 'url' | 'color' | 'select' | 'boolean';
  defaultValue?: any;
  options?: FieldOption[];
}

export interface StyleVariant {
  id: string;
  name: string;
  label: string;
  styleOverrides: Partial<BlockStyles>;
}

// ============================================================================
// DESIGN TOKEN TYPES (Phase 2)
// ============================================================================

export interface DesignToken {
  id: string;
  siteId: string;
  tokenGroup: DesignTokenGroup;
  tokenName: string;
  tokenValue: string;
  tokenLabel: string | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export type DesignTokenGroup = 'color' | 'typography' | 'spacing' | 'shadow' | 'border';

// ============================================================================
// SECTION TEMPLATE TYPES (Phase 2)
// ============================================================================

export interface SectionTemplate {
  id: string;
  templateName: string;
  description: string | null;
  category: string | null;
  blocks: ContentBlock[];
  thumbnailUrl: string | null;
  isGlobal: boolean;
  usageCount: number;
  createdAt: string;
  createdBy: string | null;
}

// ============================================================================
// CODE SNIPPET TYPES (Phase 3)
// ============================================================================

export interface CodeSnippet {
  id: string;
  snippetName: string;
  description: string | null;
  language: 'html' | 'css' | 'javascript';
  code: string;
  category: string | null;
  isGlobal: boolean;
  usageCount: number;
  createdAt: string;
  createdBy: string | null;
}

// ============================================================================
// RBAC TYPES (Phase 4)
// ============================================================================

export interface WebPresencePermission {
  id: string;
  userId: string;
  role: WebPresenceRole;
  canEditDesign: boolean;
  canEditContent: boolean;
  canPublish: boolean;
  canDelete: boolean;
  canManageSeo: boolean;
  canManageForms: boolean;
  canManageAssets: boolean;
  canUseCodeEditor: boolean;
  canManageContentTypes: boolean;
  siteIds: string[] | null;
  createdAt: string;
  updatedAt: string;
}

export type WebPresenceRole = 'designer' | 'content_editor' | 'publisher' | 'viewer';

export interface ActivityLogEntry {
  id: string;
  userId: string;
  action: 'create' | 'update' | 'delete' | 'publish' | 'unpublish';
  entityType: 'page' | 'form' | 'asset' | 'content_item' | 'component';
  entityId: string;
  entityName: string | null;
  changes: Record<string, { old: any; new: any }> | null;
  ipAddress: string | null;
  createdAt: string;
}

// ============================================================================
// SEO REDIRECT TYPES (Phase 4)
// ============================================================================

export interface Redirect {
  id: string;
  siteId: string;
  sourcePath: string;
  targetUrl: string;
  redirectType: 301 | 302;
  isActive: boolean;
  hitCount: number;
  lastHitAt: string | null;
  createdAt: string;
  createdBy: string | null;
}

// ============================================================================
// AI DESIGN ASSISTANT TYPES
// ============================================================================

export type AIContentType =
  | 'web_page'
  | 'linkedin_post'
  | 'flyer'
  | 'email'
  | 'landing_page'
  | 'social_post'
  | 'sms'
  | 'voice_ai_script';

export type AIOutputFormat = 'blocks' | 'copy';

export type AIGenerationStatus =
  | 'pending'
  | 'completed'
  | 'failed'
  | 'applied'
  | 'published';

export interface AIGenerationRequest {
  content_type: AIContentType;
  prompt: string;
  context?: AIGenerationContext;
  model_preference?: 'fast' | 'quality';
  system_prompt_override?: string;
  site_id?: string;
  page_id?: string;
}

export interface AIGenerationContext {
  brand_settings?: Record<string, unknown>;
  design_tokens?: Record<string, unknown>[];
  org_name?: string;
  tone?: string;
  audience?: string;
  program_data?: Record<string, unknown>;
  existing_content?: Record<string, unknown>;
  target_keywords?: string[];
}

export interface AIGeneratedBlocks {
  blocks: ContentBlock[];
  seo?: {
    metaTitle?: string;
    metaDescription?: string;
    ogTitle?: string;
    ogDescription?: string;
  };
  suggestedSlug?: string;
  conversionGoal?: string;
  printSettings?: { size: string; orientation: string };
}

export interface AIGeneratedCopy {
  copy: string;
  subject?: string;
  previewText?: string;
  hashtags?: string[];
  callToAction?: string;
  ctaText?: string;
  ctaUrl?: string;
  suggestedImagePrompt?: string;
  suggestedSendTime?: string;
  platform?: string;
}

export interface AIGeneration {
  id: string;
  siteId: string | null;
  pageId: string | null;
  contentType: AIContentType;
  outputFormat: AIOutputFormat;
  prompt: string;
  refinementOf: string | null;
  generated: AIGeneratedBlocks | AIGeneratedCopy;
  meta: {
    model: string;
    input_tokens: number;
    output_tokens: number;
    duration_ms: number;
  };
  status: AIGenerationStatus;
  appliedToPageId: string | null;
  appliedAt: string | null;
  createdAt: string;
}

export interface AITemplate {
  id: string;
  templateName: string;
  contentType: AIContentType;
  description: string | null;
  systemPrompt: string;
  exampleOutput: Record<string, unknown>;
  defaultModel: string;
  maxTokens: number;
  category: string | null;
  tags: string[];
  usageCount: number;
  avgRating: number | null;
  isGlobal: boolean;
  isActive: boolean;
  createdAt: string;
}

export interface AIRefineRequest {
  generation_id: string;
  feedback: string;
  context?: AIGenerationContext;
}

export interface AIApplyRequest {
  generation_id: string;
  page_id: string;
  merge_mode?: 'replace' | 'append';
}

export const AI_CONTENT_TYPE_OPTIONS: {
  value: AIContentType;
  label: string;
  description: string;
  icon: string;
  outputFormat: AIOutputFormat;
}[] = [
  {
    value: 'web_page',
    label: 'Web Page',
    description: 'Full page with structured sections',
    icon: 'Globe',
    outputFormat: 'blocks',
  },
  {
    value: 'landing_page',
    label: 'Landing Page',
    description: 'Conversion-focused campaign page',
    icon: 'Target',
    outputFormat: 'blocks',
  },
  {
    value: 'linkedin_post',
    label: 'LinkedIn Post',
    description: 'Professional social media post',
    icon: 'Linkedin',
    outputFormat: 'copy',
  },
  {
    value: 'flyer',
    label: 'Flyer / One-Pager',
    description: 'Print-friendly promotional flyer',
    icon: 'FileText',
    outputFormat: 'blocks',
  },
  {
    value: 'email',
    label: 'Email Campaign',
    description: 'Member outreach email',
    icon: 'Mail',
    outputFormat: 'copy',
  },
  {
    value: 'social_post',
    label: 'Social Post',
    description: 'Short social media post',
    icon: 'Share2',
    outputFormat: 'copy',
  },
  {
    value: 'sms',
    label: 'SMS Message',
    description: 'Short text message (160 chars)',
    icon: 'MessageSquare',
    outputFormat: 'copy',
  },
  {
    value: 'voice_ai_script',
    label: 'Voice AI Script',
    description: 'AI voice call script',
    icon: 'Mic',
    outputFormat: 'copy',
  },
];

// ============================================================================
// CONTENT OUTPUT TYPES — managed lifecycle for all generated content
// ============================================================================

export type ContentOutputStatus = 'draft' | 'ready' | 'published' | 'sent' | 'archived';

export type PublishChannel = 'site' | 'linkedin' | 'facebook' | 'instagram' | 'twitter' | 'email' | 'print' | 'pdf';

export interface ContentOutput {
  id: string;
  generationId: string;
  contentType: AIContentType;
  title: string;
  content: AIGeneratedBlocks | AIGeneratedCopy;
  status: ContentOutputStatus;
  channel?: PublishChannel;
  publishedAt?: string;
  scheduledAt?: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}
